import { asSystemId } from './ids.js';
import type { DomainGroup, Maturity, Platform, PlatformAvailability } from './taxonomy.js';
import type { PrivacyClass } from './privacy.js';
import type { SystemMetadata, SystemSurface } from './system-metadata.js';
import { InMemoryFeatureRegistry, type FeatureRegistry } from './feature-registry.js';

/**
 * THE GROWTH BACKBONE.
 *
 * Every Life OS capability is one registry entry here. Navigation, Search, and
 * All Systems all derive from this list, so a new system is automatically
 * discoverable and can never hide. Doctors enforce complete, honest metadata.
 *
 * To grow Life OS: add a `sys(...)` entry (and, if it should appear in the
 * domain sidebar, a node in `navigation.ts`). That is the entire contract.
 */

const A: PlatformAvailability = 'available';
const P: PlatformAvailability = 'planned';

/** web-first availability: live on web, planned elsewhere. */
function webFirst(): Record<Platform, PlatformAvailability> {
  return { web: A, linux: P, windows: P, android: P, ios: P };
}

interface SysOpts {
  id: string;
  label: string;
  purpose: string;
  group: DomainGroup;
  maturity: Maturity;
  route: string;
  privacy?: PrivacyClass;
  pinned?: boolean;
  recent?: boolean;
  admin?: boolean;
  preserve?: boolean;
}

function sys(o: SysOpts): SystemMetadata {
  const surface: SystemSurface = { kind: 'route', path: o.route };
  return {
    id: asSystemId(o.id),
    label: o.label,
    shortPurpose: o.purpose,
    group: o.group,
    maturity: o.maturity,
    surface,
    privacyClass: o.privacy ?? 'sensitive_private',
    platformAvailability: webFirst(),
    pinnedEligible: o.pinned ?? false,
    recentEligible: o.recent ?? true,
    adminOrArchive: o.admin ?? false,
    ...(o.preserve ? { sourcePreservation: 'preserve-before-promote' as const } : {}),
  };
}

/** Calm top-level operating surfaces (always visible, bespoke screens). */
export const dailySurfaces: readonly SystemMetadata[] = [
  sys({ id: 'today', label: 'Today', purpose: 'What should I do now?', group: 'daily', maturity: 'daily', route: '/today', pinned: true }),
  sys({ id: 'capture', label: 'Capture', purpose: 'Record anything without deciding its structure yet.', group: 'daily', maturity: 'daily', route: '/capture', pinned: true, preserve: true }),
  sys({ id: 'tasks', label: 'Tasks', purpose: 'Execute and close loops: Now, Next, Waiting, Scheduled, Done.', group: 'daily', maturity: 'daily', route: '/tasks', pinned: true }),
  sys({ id: 'search', label: 'Search', purpose: 'Find anything; answer "where is it?"', group: 'daily', maturity: 'daily', route: '/search', privacy: 'sensitive_private' }),
  sys({ id: 'all-systems', label: 'All Systems', purpose: 'The directory of everything; answer "what exists?"', group: 'daily', maturity: 'daily', route: '/all-systems', privacy: 'standard_private' }),
];

/** Daily action systems (the operating engine). Hubs render until bespoke screens land. */
export const actionSystems: readonly SystemMetadata[] = [
  sys({ id: 'calendar', label: 'Calendar & Schedule', purpose: 'Place events, tasks, routines, and reminders in time.', group: 'daily', maturity: 'supported', route: '/calendar' }),
  sys({ id: 'routines', label: 'Routines & Habits', purpose: 'Recurring systems, habits, standards — distinct but linkable.', group: 'daily', maturity: 'supported', route: '/routines' }),
  sys({ id: 'targets', label: 'Targets & Streaks', purpose: 'Measured commitments with consistency evidence — proof, not shame.', group: 'daily', maturity: 'supported', route: '/targets' }),
  sys({ id: 'reminders', label: 'Reminders & Notifications', purpose: 'Local-first reminders with delivery state, snooze, and quiet hours.', group: 'bridges', maturity: 'supported', route: '/reminders', privacy: 'standard_private' }),
  sys({ id: 'reviews', label: 'Reviews', purpose: 'Daily, weekly, and monthly review loops turn data into decisions.', group: 'daily', maturity: 'supported', route: '/reviews' }),
  sys({ id: 'focus', label: 'Focus & Pomodoro', purpose: 'Run focused sessions linked to real work.', group: 'work', maturity: 'incubator', route: '/focus' }),
];

/** Direction layer: vision, roles, values, challenges, opportunities. */
export const directionSystems: readonly SystemMetadata[] = [
  sys({ id: 'direction', label: 'Direction', purpose: 'Connect vision, roles, values, and meaning to the next move — without pressure.', group: 'direction', maturity: 'incubator', route: '/direction' }),
  sys({ id: 'direction-roles', label: 'Roles & Relationships', purpose: 'Son, founder, learner, friend — context and care, never a scoreboard.', group: 'direction', maturity: 'incubator', route: '/direction/roles' }),
  sys({ id: 'direction-values', label: 'Values & Mindsets', purpose: 'Beliefs and mindsets linked to systems, habits, and evidence.', group: 'direction', maturity: 'incubator', route: '/direction/values' }),
  sys({ id: 'direction-challenges', label: 'Challenges & Opportunities', purpose: 'Choose the next move from challenges, responsibilities, and opportunities.', group: 'direction', maturity: 'incubator', route: '/direction/challenges' }),
];

export const repairSystems: readonly SystemMetadata[] = [
  sys({ id: 'problems', label: 'Problems & Repair', purpose: 'Diagnose blockers without shame: cause, intervention, evidence, next step.', group: 'repair', maturity: 'incubator', route: '/problems' }),
];

/** Domain hubs (deep work). Each is browsable now; depth grows over time. */
export const domainSystems: readonly SystemMetadata[] = [
  sys({ id: 'projects', label: 'Projects', purpose: 'Move projects from idea to proof; one canonical project, many lenses.', group: 'projects', maturity: 'supported', route: '/projects' }),
  sys({ id: 'knowledge', label: 'Knowledge', purpose: 'Notes + semantic graph; preserve sources, learn through prerequisites.', group: 'knowledge', maturity: 'supported', route: '/knowledge' }),
  sys({ id: 'knowledge-notes', label: 'Notes', purpose: 'Markdown notes with tags, backlinks, and preserved provenance.', group: 'knowledge', maturity: 'incubator', route: '/knowledge/notes' }),
  sys({ id: 'knowledge-graph', label: 'Knowledge Graph', purpose: 'Prerequisites, used-in, mastery, and learning paths.', group: 'knowledge', maturity: 'incubator', route: '/knowledge/graph' }),
  sys({ id: 'source-review', label: 'Source Review', purpose: 'Preserve and triage imported sources without losing raw notes.', group: 'knowledge', maturity: 'supported', route: '/source-review', pinned: true, preserve: true }),
  sys({ id: 'study', label: 'Study & Learning', purpose: 'Programs, subjects, sessions, and what to learn next.', group: 'study', maturity: 'supported', route: '/study' }),
  sys({ id: 'academics', label: 'Academics & Funding', purpose: 'Education, university admissions, deadlines, and how study gets funded.', group: 'study', maturity: 'supported', route: '/academics', privacy: 'sensitive_private' }),
  sys({ id: 'career', label: 'Career & Identity', purpose: 'Profile, portfolio, opportunities, applications, and proof.', group: 'career', maturity: 'supported', route: '/career' }),
  sys({ id: 'health', label: 'Health & Body', purpose: 'Sleep, recovery, movement, energy — wellness signals, not medical truth.', group: 'health', maturity: 'supported', route: '/health' }),
  sys({ id: 'finance', label: 'Finance Vault', purpose: 'Money clarity with raw data kept private and vault-gated.', group: 'finance', maturity: 'supported', route: '/finance', privacy: 'finance_vault' }),
  sys({ id: 'business', label: 'Business & Ventures', purpose: 'Owner-level portfolio of companies, ventures, and ideas.', group: 'business', maturity: 'incubator', route: '/business' }),
  sys({ id: 'work', label: 'Work & Content', purpose: 'Streams, deliverables, sessions, and public signal.', group: 'work', maturity: 'incubator', route: '/work' }),
  sys({ id: 'mental', label: 'Mental', purpose: 'Journal, reading, models, and cognitive systems — strictly private.', group: 'mental', maturity: 'incubator', route: '/mental', privacy: 'strict_local' }),
  sys({ id: 'spiritual', label: 'Spiritual', purpose: 'Gratitude, meditation, prayer, reflection, and purpose.', group: 'spiritual', maturity: 'incubator', route: '/spiritual' }),
  sys({ id: 'environment', label: 'Environment & Devices', purpose: 'Workspace, devices, inventory, and attention.', group: 'environment', maturity: 'incubator', route: '/environment', privacy: 'standard_private' }),
];

/** Intelligence + runtime + security. */
export const systemSystems: readonly SystemMetadata[] = [
  sys({ id: 'assistant', label: 'Assistant', purpose: 'Reviewable, permissioned help. Life OS stays the system of record.', group: 'assistant', maturity: 'incubator', route: '/assistant', privacy: 'standard_private' }),
  sys({ id: 'runtime-status', label: 'Runtime Status', purpose: 'Trust local-first storage, sync, backup, and platform health.', group: 'runtime', maturity: 'admin', route: '/runtime', privacy: 'standard_private', admin: true }),
  sys({ id: 'security', label: 'Security & Lock', purpose: 'App lock, login, OTP, trusted devices, and vault step-up.', group: 'runtime', maturity: 'admin', route: '/security', privacy: 'strict_local', admin: true }),
];

/** The full seeded system list. Append here (or new domain arrays) to grow. */
export const foundationSystems: readonly SystemMetadata[] = [
  ...dailySurfaces,
  ...actionSystems,
  ...directionSystems,
  ...repairSystems,
  ...domainSystems,
  ...systemSystems,
];

/** Build a registry preloaded with the seeded systems. */
export function createFoundationRegistry(): FeatureRegistry {
  return new InMemoryFeatureRegistry(foundationSystems);
}

/** Routes that have a bespoke implemented screen (everything else uses a hub). */
export const bespokeRoutes: readonly string[] = [
  '/today',
  '/capture',
  '/tasks',
  '/search',
  '/all-systems',
  '/source-review',
  '/runtime',
  '/calendar',
  '/reviews',
  '/targets',
  '/routines',
  '/problems',
  '/projects',
  '/finance',
  '/direction',
  '/direction/roles',
  '/direction/values',
  '/direction/challenges',
  '/health',
  '/assistant',
  '/study',
  '/academics',
  '/career',
  '/mental',
  '/spiritual',
  '/work',
  '/business',
  '/environment',
  '/focus',
  '/reminders',
  '/security',
  '/knowledge/notes',
  '/knowledge/graph',
];
